import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core'
import { EntityFilterTile } from '../../models/entity-configuration'
import { Observable, Subscription } from 'rxjs'
import { EntityTileService } from '../../services/entity-tile.service'
import { filter, switchMap, take, tap } from 'rxjs/operators'
import { AdditionalQualifier } from '../../models/table-state.model'
import { TableData } from '../../models/table-data'
import { FilterTileRefreshService } from '../../services/filter-tile-refresh.service'
import { SoftFilter } from '../../models/softfilter'
import { BaseNavStateComponent, JmlNavAdapterService } from '../../../navigation-state'
import { EntityTileState } from './entity-tile.state'
import { ActivatedRoute, Router } from '@angular/router'

@Component({
  selector: 'jml-lib-entity-tile',
  templateUrl: './entity-tile.component.html',
  styleUrls: ['./entity-tile.component.scss']
})
export class EntityTileComponent extends BaseNavStateComponent<EntityTileState> implements OnInit, OnDestroy {
  @Input() tableData: TableData

  @Output() updateAdditionalQualifier = new EventEmitter<AdditionalQualifier>()

  isChecked: boolean[] = [] // selected tiles
  disabledTiles: boolean[] = [] // tiles with count = 0

  initialLoadCompleted: boolean[] = []

  entityFilterTiles$: Observable<EntityFilterTile[]>
  entityFilterTiles: EntityFilterTile[]

  subscriptions: Subscription[] = []

  constructor(
    private data: EntityTileService,
    private refreshService: FilterTileRefreshService,
    jmlNav: JmlNavAdapterService,
    route: ActivatedRoute,
    router: Router
  ) {
    super(jmlNav, route, router)
  }

  ngOnInit(): void {
    // Get filterTiles form config to be immediately displayed without count
    // this.subscriptions.push(this.data.getEntityFilterTiles$(this.tableData.entityType).subscribe(result => {
    //   this.entityFilterTiles = result;
    // }));

    // is this component a top level comp (is this component referenced in the route)?
    const isRouteComp = false
    // will this component provide a custom identifier?
    const hasCustomComponentIdentifier = true
    // initialize pre-ngOnInit-setting for base class.
    this.initNavStateSettings(isRouteComp, hasCustomComponentIdentifier)

    // set component identifier to be used for identifying the component state.
    this.setComponentIdentifier(`EntityTile_${this.tableData.entityType}`)

    // initializing base-class logic
    super.ngOnInit()

    // loading previous state when navigation back in the browser.
    this.subscriptions.push(
      this.data
        .getEntityFilterTiles$(this.tableData.entityType)
        .pipe(
          tap((result) => {
            this.entityFilterTiles = result
          }),
          switchMap((_) => this.initWithExistingState$),
          take(1),
          filter((state) => !!state)
        )
        .subscribe((result) => {
          result.isChecked.forEach((element, index) => {
            if (element) {
              // this.toggleCheck(index);
              this.isChecked[index] = true
            }
          })
        })
    )

    this.entityFilterTiles.forEach((tile, index) => {
      //concat hard and soft filter
      var filterExpression = [tile.filterExpression, tile.softFilterExpression].filter(Boolean).join(', ')

      this.data
        .getCountOfEntityFilter$(this.tableData.entityType, filterExpression)
        .pipe(take(1))
        .subscribe((result) => {
          tile.count = result.count
          tile.count <= 0 ? (this.disabledTiles[index] = true) : (this.disabledTiles[index] = false)
          this.initialLoadCompleted[index] = true
        })
    })

    // update the filterTilesCount each time the Subject of the service is updated
    this.entityFilterTiles$ = this.refreshService.refreshFilterTile$.pipe(
      switchMap((_) => this.data.getEntityFilterTiles$(this.tableData.entityType))
    )

    // block exectues on refresh button click in dynamic table
    this.subscriptions.push(
      this.entityFilterTiles$.subscribe((entityFilterTiles) => {
        this.entityFilterTiles = entityFilterTiles

        this.initialLoadCompleted.forEach((initialLoadComplete, index) => {
          this.initialLoadCompleted[index] = false
        })

        this.entityFilterTiles.forEach((tile, index) => {
          let filterExpression = [tile.filterExpression, tile.softFilterExpression].filter(Boolean).join(', ')
          this.data
            .getCountOfEntityFilter$(this.tableData.entityType, filterExpression)
            .pipe(take(1))
            .subscribe((result) => {
              tile.count = result.count
              tile.count <= 0 ? (this.disabledTiles[index] = true) : (this.disabledTiles[index] = false)
              this.initialLoadCompleted[index] = true
            })
        })
      })
    )
  }

  toggleCheck(selectedTile: number) {
    this.isChecked[selectedTile] = !this.isChecked[selectedTile]

    // garuantees that only one tile is selected at the same time
    if (this.isChecked[selectedTile]) {
      this.isChecked.forEach((element, index) => {
        if (index !== selectedTile) {
          this.isChecked[index] = false
        }
      })
      // Send filter to dynamic table and refresh
      this.tableData.setFilters(
        new AdditionalQualifier(this.entityFilterTiles[selectedTile].filterExpression),
        new SoftFilter(undefined, this.entityFilterTiles[selectedTile].softFilterExpression)
      )

      let filterExpression = [
        this.entityFilterTiles[selectedTile].filterExpression,
        this.entityFilterTiles[selectedTile].softFilterExpression
      ]
        .filter(Boolean)
        .join(', ')

      // this is very inefficient since the dynamic table receives the count with the filter-Api-Call --> maybe we can use this information here with databinding
      this.data
        .getCountOfEntityFilter$(this.tableData.entityType, filterExpression)
        .pipe(take(1))
        .subscribe((result) => {
          this.entityFilterTiles[selectedTile].count = result.count
        })
    } else {
      this.tableData.setFilters(null, null)
    }

    //saving page state
    let state = new EntityTileState()
    state.isChecked = [...this.isChecked]
    this.saveState(state, false)
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe())
    super.ngOnDestroy()
  }
}
